import React, { useState, ComponentType } from 'react';
import styled from '@emotion/styled';
import { Flex, Box } from 'reflexbox';
import pluralize from 'pluralize';
import { FontAwesome } from '../../components/FontAwesome';
import { TopBar } from '../../components/Marketing/TopBar';
import { Banner } from './components/Banner';
import { Footer } from '../../components/Marketing/Footer';
import { Filters } from './components/Filters';
import {
  ConnectedTopicsFilter,
  ConnectedInstitutionsFilter,
  ConnectedFeeTypeFilter,
  ConnectedCourseTypesFilter,
  ConnectedCourseSectorsFilter,
  ConnectedCourseLanguagesFilter,
} from './components/Filters/Filter/connector';
import { Popup } from './components/Popup';
import { ConnectedFilteredCourses } from './components/FilteredCourses/connector';
import { MARKETING_PAGE_BREAKPOINT_DESKTOP } from '../../components/Style/breakpoints';
import { MarketingPageGlobalStyling } from '../../components/MarketingPageGlobalStyling/component';
import { CoursesPageProps } from './types';
import { ConnectedUniCard } from './components/UniCard/connector';
import { FeaturedCourses } from '../../components/Marketing/FeaturedCourses';

const StyledButton = styled.button`
  font-family: Avenir, sans-serif;
  font-style: normal;
  font-weight: 800;
  font-size: 16px;
  line-height: 140%;
  color: #000000;
  width: 100%;
  background: transparent;
  outline: none;
  border: 0;
  padding: 12px;
  cursor: pointer;
  :hover {
    opacity: 0.9;
  }
`;

const FilterSearchContainer = styled(Box)`
  border-bottom: 1px solid #e5eaeb;
  @media only screen and (min-width: ${MARKETING_PAGE_BREAKPOINT_DESKTOP}) {
    display: none;
  }
`;

const Left = styled(Box)`
  display: none;
  @media only screen and (min-width: ${MARKETING_PAGE_BREAKPOINT_DESKTOP}) {
    display: block;
    min-width: 270px;
  }
`;

const Right = styled(Box)`
  flex-grow: 1;
  padding: 24px 16px;
  @media only screen and (min-width: ${MARKETING_PAGE_BREAKPOINT_DESKTOP}) {
    padding-top: 45px;
    max-width: 1140px;
    margin: 0 auto;
  }
  @media only screen and (min-width: 1398px) {
    padding-left: 60px;
    padding-right: 60px;
    max-width: 1140px;
    margin: 0 auto;
  }
`;

const Content = styled(Flex)`
  flex-grow: 1;
`;

const UniCardContainer = styled(Box)`
  display: none;
  margin-bottom: 30px;
  @media only screen and (min-width: ${MARKETING_PAGE_BREAKPOINT_DESKTOP}) {
    display: block;
  }
`;

const CoursesDisplayContainer = styled(Flex)`
  position: relative;
  flex-direction: column;
`;

const TotalResults = styled(Flex)`
  font-family: Avenir;
  font-style: normal;
  font-size: 24px;
  font-weight: lighter;
  margin: 1em 0;
  color: #041416;
  text-align: center;
  align-items: center;
  justify-content: center;
`;

const FilterProcessing = styled(Flex)`
  padding: 0px 20px 20px 20px;
`;

const FilterProcessingText = styled('span')`
  font-family: Avenir;
  font-style: normal;
  line-height: 1.5;
  text-align: center;
  width: 100%;
`;

const FilterProcessingTitle = styled('h4')`
  color: #61beb2;
  font-size: 22px;
  font-weight: bold;
  color: #000000;
  margin: 0;
`;

const FilterProcessingRefresh = styled('span')`
  cursor: pointer;
  text-decoration: underline;
`;

const SpinnerIcon = styled('span')`
  margin-right: 10px;
  font-size: 22px;
  color: #000000;
`;

const PopulatedFilters: ComponentType = () => {
  return (
    <Filters>
      <ConnectedTopicsFilter />
      <ConnectedInstitutionsFilter />
      <ConnectedCourseTypesFilter />
      <ConnectedCourseSectorsFilter />
      <ConnectedFeeTypeFilter />
      <ConnectedCourseLanguagesFilter />
    </Filters>
  );
};

export const CoursesPage: ComponentType<CoursesPageProps> = ({
  isLoggedIn,
  hasFiltersSelected,
  totalResults,
  processing,
  error,
  featuredCourses,
  onRefreshClick,
}) => {
  const [showPopup, setShowPopup] = useState(false);
  const filterHandler = (): void => {
    setShowPopup(true);
  };
  const handleClose = (): void => {
    setShowPopup(false);
  };
  return (
    <>
      <MarketingPageGlobalStyling />
      {isLoggedIn ? null : <TopBar />}
      <Banner />
      <FilterSearchContainer>
        <StyledButton onClick={filterHandler}>View filters</StyledButton>
        <Popup isShown={showPopup} title="Filters" handleClose={handleClose}>
          <PopulatedFilters />
        </Popup>
      </FilterSearchContainer>
      <Content>
        <Left>
          <PopulatedFilters />
        </Left>
        <Right>
          {!hasFiltersSelected && !processing && (
            <FeaturedCourses
              header="Featured courses"
              courses={featuredCourses}
              showViewAll={false}
            />
          )}
          <UniCardContainer>
            <ConnectedUniCard />
          </UniCardContainer>
          {hasFiltersSelected && !processing && (
            <TotalResults>
              {pluralize('course', totalResults, true)} matching
            </TotalResults>
          )}
          <CoursesDisplayContainer>
            {processing && (
              <FilterProcessing>
                {error ? (
                  <FilterProcessingText>
                    <FilterProcessingTitle>{error}</FilterProcessingTitle>
                    <FilterProcessingRefresh onClick={onRefreshClick}>
                      Click here to refresh
                    </FilterProcessingRefresh>
                  </FilterProcessingText>
                ) : (
                  <FilterProcessingText>
                    <FilterProcessingTitle>
                      <SpinnerIcon>
                        <FontAwesome name="spinner" spin />
                      </SpinnerIcon>
                      Retrieving courses
                    </FilterProcessingTitle>
                  </FilterProcessingText>
                )}
              </FilterProcessing>
            )}
            {hasFiltersSelected && <ConnectedFilteredCourses />}
          </CoursesDisplayContainer>
        </Right>
      </Content>
      {isLoggedIn ? null : <Footer />}
    </>
  );
};
